import type { MouseEvent, ReactNode, Ref } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLanguage, getMarket } from '@travel/i18n';
import { isMacOs } from '@travel/traveler-core/utils';
import { ariaOnKeyDown, cx } from '@travel/utils';

import styles from './link.module.scss';

export type Props = {
  to: string;
  children: ReactNode;
  innerRef?: Ref<HTMLAnchorElement>;
  isLink?: boolean;
} & React.HTMLProps<HTMLAnchorElement>;

export type LinkDependencies = {
  pushLocation: (to: string) => any;
  getHost: (state: any) => string;
  getProtocol: (state: any) => string | undefined;
};

export const getHref = (
  host: string,
  to: string,
  language: string,
  marketCode: string,
  protocol?: string,
) => {
  return `${protocol ? `${protocol}://` : ''}${host}${to}`;
};

export function Link(dependencies: LinkDependencies) {
  const { pushLocation, getHost, getProtocol } = dependencies;

  function LinkComponent(props: Props) {
    const { to, className, isLink, href, innerRef, ...restProps } = props;
    const dispatch = useDispatch();
    const host = useSelector(getHost);
    const protocol = useSelector(getProtocol);
    const language = useSelector(getLanguage);
    const market = useSelector(getMarket);

    const handleClick = (e: MouseEvent | React.KeyboardEvent<HTMLAnchorElement>) => {
      const openInNewTab = isMacOs() ? e.metaKey : e.ctrlKey;
      if (!openInNewTab && props.onClick) {
        props.onClick(e as MouseEvent<HTMLAnchorElement>);
      } else if (!openInNewTab) {
        e.preventDefault();
        dispatch(pushLocation(to));
      }
    };

    return (
      <a
        className={cx(!isLink && styles.wrapper, className)}
        {...restProps}
        onClick={handleClick}
        href={href || getHref(host, to, language, market.marketCode, protocol)}
        role="button"
        onKeyDown={ariaOnKeyDown(
          // incase a onClick prop is added we don't run the handleClick function
          (props.onClick as (e: MouseEvent | React.KeyboardEvent<HTMLAnchorElement>) => void) ||
            handleClick,
        )}
        tabIndex={0}
        ref={innerRef}
      >
        {props.children}
      </a>
    );
  }

  return LinkComponent;
}

export default Link;
